/**
 * schedule.ts — reading-interval / status logic.
 * MUST stay behaviourally identical to apps-script/Schedule.gs.
 *
 * Two independent concepts (never merge them):
 *   minIntervalMin    — HARD GATE. A submission is refused while (now - last) < minIntervalMin.
 *   targetIntervalMin — advisory schedule. Drives "next recommended" and the status chip only.
 *
 * Bands (m = minutes since the last effective reading):
 *   m <  minInterval                       -> BLOCKED     canSubmit = false
 *   minInterval <= m < target - dueGrace   -> EARLY_OK    canSubmit = true
 *   target - dueGrace <= m <= overdueAfter -> DUE         canSubmit = true
 *   m > overdueAfter                       -> OVERDUE     canSubmit = true
 *   no previous reading                    -> NO_HISTORY  canSubmit = true
 */

export type ReadingStatus = 'NO_HISTORY' | 'BLOCKED' | 'EARLY_OK' | 'DUE' | 'OVERDUE';

export interface ScheduleConfig {
  minIntervalMin: number;
  targetIntervalMin: number;
  dueGraceMin: number;
  overdueAfterMin: number;
}

export interface ScheduleState {
  status: ReadingStatus;
  canSubmit: boolean;
  minutesSinceLast: number | null;
  lastReadingAt: string | null;
  /** ISO time at which a BLOCKED reading becomes allowed (last + minInterval). */
  nextAvailableAt: string | null;
  /** ISO time of the recommended next reading (last + targetInterval). */
  nextRecommendedAt: string | null;
  reason: string;
}

const MIN = 60_000;

export function evaluateSchedule(
  now: Date,
  lastReadingAt: Date | null,
  cfg: ScheduleConfig,
): ScheduleState {
  const nowMs = now.getTime();

  if (!lastReadingAt) {
    return {
      status: 'NO_HISTORY',
      canSubmit: true,
      minutesSinceLast: null,
      lastReadingAt: null,
      nextAvailableAt: null,
      nextRecommendedAt: new Date(nowMs).toISOString(),
      reason: 'No previous reading on record. First reading can be entered now.',
    };
  }

  const lastMs = lastReadingAt.getTime();
  const mins = (nowMs - lastMs) / MIN;
  const base = {
    minutesSinceLast: Math.round(mins * 10) / 10,
    lastReadingAt: new Date(lastMs).toISOString(),
    nextAvailableAt: new Date(lastMs + cfg.minIntervalMin * MIN).toISOString(),
    nextRecommendedAt: new Date(lastMs + cfg.targetIntervalMin * MIN).toISOString(),
  };

  if (mins < cfg.minIntervalMin) {
    return { ...base, status: 'BLOCKED', canSubmit: false,
      reason: `Minimum ${cfg.minIntervalMin} minutes between readings has not elapsed.` };
  }
  if (mins < cfg.targetIntervalMin - cfg.dueGraceMin) {
    return { ...base, status: 'EARLY_OK', canSubmit: true,
      reason: `Earlier than the ${cfg.targetIntervalMin}-minute target, but allowed.` };
  }
  if (mins <= cfg.overdueAfterMin) {
    return { ...base, status: 'DUE', canSubmit: true, reason: 'Reading is due.' };
  }
  return { ...base, status: 'OVERDUE', canSubmit: true,
    reason: 'Reading is overdue. Submit as soon as possible; the actual time is recorded.' };
}

export const DEFAULT_SCHEDULE_CONFIG: ScheduleConfig = {
  minIntervalMin: 60,
  targetIntervalMin: 120,
  dueGraceMin: 15,
  overdueAfterMin: 150,
};

/** Short human label for a status, for chips/badges. */
export function statusLabel(s: ReadingStatus): string {
  switch (s) {
    case 'NO_HISTORY': return 'Reading due';
    case 'BLOCKED': return 'Not available yet';
    case 'EARLY_OK': return 'Early reading allowed';
    case 'DUE': return 'Reading due';
    case 'OVERDUE': return 'Reading overdue';
  }
}
